import { FC, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from "@/integrations/supabase/client";
import { Button } from './ui/button';
import { LessonQuiz } from './lesson/LessonQuiz';
import { toast } from './ui/use-toast';
import { QuizQuestion } from './admin/lesson/types';
import { LessonHeader } from './lesson/LessonHeader';
import { LessonContentSection } from './lesson/LessonContentSection';
import { useAuth } from "../hooks/useAuth";
import { useSubscription } from "../hooks/useSubscription";
import { Crown, Lock } from 'lucide-react';

interface Lesson {
  id: string;
  title: string;
  video_url: string;
  content_text: string;
  audio_url: string;
  level: string;
  age_group: string;
  is_premium: boolean;
}

export const LessonContent: FC = () => {
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const [showQuiz, setShowQuiz] = useState(false);
  const { user } = useAuth();
  const { subscription, hasAccess, level, accessibleLevels } = useSubscription();

  console.log('LessonContent rendered with id:', lessonId);

  // Fetch lesson data
  const { data: lesson, isLoading: isLoadingLesson, error: lessonError } = useQuery({
    queryKey: ['lesson', lessonId],
    queryFn: async () => {
      if (!lessonId) {
        throw new Error('No lesson ID provided');
      }

      const { data: lessonData, error: lessonError } = await supabase
        .from('lessons')
        .select('*')
        .eq('id', lessonId)
        .single();

      if (lessonError) throw lessonError;
      if (!lessonData) throw new Error('Lección no encontrada');

      console.log('LessonContent - Datos de la lección:', {
        id: lessonData.id,
        title: lessonData.title,
        video_url: lessonData.video_url,
        audio_url: lessonData.audio_url
      });

      return lessonData;
    },
    retry: false
  });

  // Fetch quiz questions if lesson exists
  const { data: questions = [], isLoading: isLoadingQuestions } = useQuery({
    queryKey: ['questions', lesson?.id],
    queryFn: async () => {
      if (!lesson?.id) return [];

      const { data, error } = await supabase
        .from('quiz_questions')
        .select('*')
        .eq('lesson_id', lesson.id)
        .eq('active', true);

      if (error) throw error;
      return data || [];
    },
    enabled: !!lesson?.id
  });

  useEffect(() => {
    if (lessonError) {
      toast({
        title: "Error",
        description: "No se pudo cargar la lección",
        variant: "destructive",
      });
      navigate('/lessons');
    }
  }, [lessonError, navigate]);

  if (isLoadingLesson) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="text-center py-10">
        <h2 className="text-2xl font-bold mb-4">Lección no encontrada</h2>
        <Button onClick={() => navigate('/lessons')}>
          Volver a lecciones
        </Button>
      </div>
    );
  }

  const isPremiumUser = user?.email === 'welingtondesosa@gmail.com' || hasAccess;

  // Determinar si el usuario tiene acceso al nivel de la lección
  const hasAccessToLevel = () => {
    if (isPremiumUser) return true; // Admin tiene acceso a todo
    return accessibleLevels.includes(lesson.level.toLowerCase()); // Usuarios normales solo tienen acceso a sus niveles permitidos
  };

  // Mostrar mensaje si no tiene acceso al nivel
  if (!hasAccessToLevel()) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-blue-50 to-white">
        <Lock className="w-16 h-16 text-blue-500 mb-4" />
        <h1 className="text-3xl font-bold text-blue-600 mb-4">Nivel Bloqueado</h1>
        <p className="text-gray-600 mb-8 text-center max-w-md">
          Esta lección es para nivel {lesson.level.toLowerCase()}. 
          Los usuarios no premium solo tienen acceso a las lecciones de nivel principiante.
        </p>
        <p className="text-blue-600 font-medium text-center">
          Haz clic en el botón "Premium" en la esquina superior derecha para acceder a todos los niveles.
        </p>
        <Button onClick={() => navigate('/lessons')} className="mt-6 bg-blue-500 hover:bg-blue-600">
          Volver a lecciones
        </Button>
      </div>
    );
  }

  // Mostrar mensaje si es contenido premium
  if (lesson.is_premium && !isPremiumUser) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-amber-50 to-white">
        <Crown className="w-16 h-16 text-amber-500 mb-4" />
        <h1 className="text-3xl font-bold text-amber-600 mb-4">Contenido Premium</h1>
        <p className="text-gray-600 mb-4 text-center max-w-md">
          Esta lección es parte del contenido premium. 
          Actualiza tu plan para acceder a todo el contenido exclusivo.
        </p>
        <p className="text-amber-600 font-medium text-center">
          Haz clic en el botón "Premium" en la esquina superior derecha para desbloquear todo el contenido.
        </p>
        <Button onClick={() => navigate('/lessons')} className="mt-6 bg-amber-500 hover:bg-amber-600">
          Volver a lecciones
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <LessonHeader
        title={lesson.title}
        level={lesson.level}
        isPremium={lesson.is_premium}
      />

      <LessonContentSection
        videoUrl={lesson.video_url}
        audioUrl={lesson.audio_url}
        contentText={lesson.content_text}
        lessonId={lesson.id}
      />

      {questions && questions.length > 0 && (
        <div className="mt-8">
          {showQuiz ? (
            <LessonQuiz
              questions={questions}
              onComplete={() => setShowQuiz(false)}
              lessonId={lesson.id}
            />
          ) : (
            <Button onClick={() => setShowQuiz(true)} className="w-full">
              Comenzar Quiz
            </Button>
          )}
        </div>
      )}
    </div>
  );
};